"use client";

import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid } from "recharts";

function toCurrency(num: number) {
    return "$" + num.toFixed(2).replace(/(\d)(?=(\d{3})+\.)/g, "$1,");
}

interface Props {
    principal: number;
    annualRate: number;
    years: number;
}

const generateAmortizationData = (principal: number, annualRate: number, years: number) => {
    const monthlyRate = annualRate / 100 / 12;
    const totalPayments = years * 12;

    const monthlyPayment =
        (principal * monthlyRate * Math.pow(1 + monthlyRate, totalPayments)) /
        (Math.pow(1 + monthlyRate, totalPayments) - 1);

    let balance = principal;
    const data = Array.from({ length: years }, () => ({
        year: 0,
        principal: 0,
        interest: 0,
        payment: 0,
    }));

    for (let i = 1; i <= totalPayments; i++) {
        const interestPayment = balance * monthlyRate;
        const principalPayment = monthlyPayment - interestPayment;
        balance -= principalPayment;

        const yearIndex = Math.floor((i - 1) / 12);
        data[yearIndex].year = yearIndex + 1;
        data[yearIndex].principal += principalPayment;
        data[yearIndex].interest += interestPayment;
        data[yearIndex].payment += monthlyPayment;
    }

    return data.map((d) => ({
        ...d,
        principal: Number(d.principal.toFixed(2)),
        interest: Number(d.interest.toFixed(2)),
        payment: Number(d.payment.toFixed(2)),
    }));
};

const MortgageOverTimeChart = ({ principal, annualRate, years }: Props) => {
    const data = generateAmortizationData(principal, annualRate, years);

    return (
        <div className="mt-8">
            <h3 className="text-xl font-bold text-center text-[#212459] mb-4">Your Payment Over Time</h3>
            <ResponsiveContainer width="100%" height={350}>
                <BarChart data={data}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="year" tick={{ fontSize: 11 }} tickFormatter={(v) => `${v} Yr`} />
                    <YAxis tickFormatter={(v) => `$${(v / 1000).toFixed(0)}K`} />
                    <Tooltip
                        formatter={(value: number) => toCurrency(value)}
                        labelFormatter={(label) => `Year ${label}`}
                    />
                    <Legend />

                    <Bar
                        dataKey="principal"
                        stackId="a"
                        fill="#434348"
                        name="Principal"
                        isAnimationActive={true}
                        animationDuration={1000}
                        radius={[4, 4, 0, 0]}
                        activeBar={{ fill: "#2d2d31" }}
                    />

                    <Bar
                        dataKey="interest"
                        stackId="a"
                        fill="#7cb5ec"
                        name="Interest"
                        isAnimationActive={true}
                        animationDuration={1000}
                        radius={[4, 4, 0, 0]}
                        activeBar={{ fill: "#509fe7" }}
                    />
                </BarChart>
            </ResponsiveContainer>
        </div>
    );
};

export default MortgageOverTimeChart;
